import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { SearchPageParams, CollectionPageParams, CriteoRecommendationsResponse, CriteoRequestContext } from './types';

export class CriteoClient extends ApiClient {
  private readonly _criteoBaseUrl: string;
  private readonly _criteoPartnerId: string;

  constructor({ criteoBaseUrl, criteoPartnerId }: { criteoBaseUrl: string; criteoPartnerId: string }) {
    super();
    const normalizedCriteoBaseUrl = criteoBaseUrl.trim();
    const normalizedCriteoPartnerId = criteoPartnerId.trim();

    if (!normalizedCriteoBaseUrl || !normalizedCriteoPartnerId) {
      throw new Error('CriteoClient requires both criteoBaseUrl and criteoPartnerId');
    }

    this._criteoBaseUrl = normalizedCriteoBaseUrl;
    this._criteoPartnerId = normalizedCriteoPartnerId;
  }

  protected get requestHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    };
  }

  private _fetchSponsoredProducts = async (
    extraParams: Record<string, string>,
    {
      eventType,
      pageId,
      retailerVisitorId,
      customerId,
      email,
    }: CriteoRequestContext & { eventType: string; pageId: string }
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    const queryParams = new URLSearchParams(
      Object.entries({
        'criteo-partner-id': this._criteoPartnerId,
        'retailer-visitor-id': retailerVisitorId,
        'customer-id': customerId,
        email,
        'page-id': pageId,
        'event-type': eventType,
        ...extraParams,
      }).filter((entry): entry is [string, string] => entry[0] === 'item' || !!entry[1])
    );

    return await this._fetch<CriteoRecommendationsResponse>({
      path: `${this._criteoBaseUrl}?${queryParams.toString()}`,
      method: 'GET',
    });
  };

  getSearchSponsoredProducts = async (
    params: Omit<SearchPageParams, 'eventType'>
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    return await this._fetchSponsoredProducts(
      {
        keywords: params.keywords,
        ...(params.filters ? { filters: params.filters } : {}),
      },
      { ...params, eventType: 'viewSearchResult' }
    );
  };

  getCollectionSponsoredProducts = async (
    params: Omit<CollectionPageParams, 'eventType'>
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    return await this._fetchSponsoredProducts(
      {
        category: params.collectionKey,
        ...(params.filters ? { filters: params.filters } : {}),
      },
      { ...params, eventType: 'viewCategory' }
    );
  };
}
